import React, {useCallback, useEffect, useRef, useState} from 'react';
import {produce} from 'immer';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import slugify from 'slugify';
import {useStoreActions} from 'easy-peasy';
import 'utils/types';
import generateStoryboardUrl from '../../routing/utils/generateStoryboardUrl'; // Import global types

import StoryboardMenuBar from './StoryboardMenuBar';
import Storyboard from './Storyboard';
import CellLoader from '@/components/loading/CellLoader';
import QueryHelpModal from '@/components/utils/QueryHelpModal';
import useStoryboardsData from './hooks/useStoryboardsData';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {usePlatformPermissions} from '@/utils/platformPermissions';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useData from '@/utils/useData';
import {useAlert} from '@/components/alerts';
import {useSafeNavigation} from '@/hooks/useSafeNavigation';
import i18n from 'i18n';
import {useStoreState} from 'store';
import {Chart} from '@/entities/chart';
import {Storyboard as StoryboardEntity} from '@/entities/storyboard';
import { isModuleEnabled } from '@/utils/modules';

interface StoryboardViewProps {
    activeStoryboardHashid?: string;
    activeStoryUrl?: string;
    activeStoryboardUrl?: string;
    activeStoryObject?: string;
    hideNavigation?: boolean;
}

interface ObjectNameData {
    optIn?: boolean;
    optInDate?: string;
    full_name?: string;
    name?: string;
    first_name?: string;
    last_name?: string;
    external_id?: string;
    team_name?: string;
    teamName?: string;
    externalId?: string;
    [key: string]: any;
}

interface AppConfig {
    FS_DATA_ACCESS_LIMIT_LAST_DAYS: number;
    [key: string]: any;
}

const StoryboardViewContent: React.FC<StoryboardViewProps> = React.memo((
    {
        activeStoryboardHashid = '',
        activeStoryUrl = '',
        activeStoryboardUrl = window.CONSTANTS?.storyboards?.[0]?.slug || '',
        activeStoryObject = '',
        hideNavigation = false,
    },
) => {
    const navigate = useNavigate();
    const appConfig: AppConfig = useStoreState((state: any) => state.appConfigs.items);
    const addChart = useStoreActions((actions: any) => actions.storyboards.addChart);
    const setActiveStoryboard = useStoreActions((actions: any) => actions.activeStoryboard.setActiveStoryboard);
    const setActiveDummyStory = useStoreActions((actions: any) => actions.activeDummyStory.setActiveDummyStory);
    const setActiveDummyStoryboard = useStoreActions((actions: any) => actions.activeDummyStoryboard.setActiveDummyStoryboard);
    const setActiveStoryObject = useStoreActions((actions: any) => actions.activeStoryObject.setActiveStoryObject);
    const setInGridAnimation = useStoreActions((actions: any) => actions.storyboardGridAnimation.setInGridAnimation);

    const inGridAnimation: boolean = useStoreState((state: any) => state.storyboardGridAnimation.inAnimation);

    const [chartSettingsModalOpen, setChartSettingsModalOpen] = useState<boolean>(false);
    const [isRearrangeMode, setIsRearrangeMode] = useState<boolean>(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const [originalChartOrder, setOriginalChartOrder] = useState<Chart[]>([]);

    const activeStoryboardIndex: number = useStoreState((state: any) => state.activeStoryboard.activeIndex);
    const activeStoryboardId: number = useStoreState((state: any) => state.activeStoryboard.activeId);

    const userIsStoryAdmin: boolean = useStoreState((state: any) => state.storyboards.userIsStoryAdmin);
    const storyIsPublic: boolean = useStoreState((state: any) => state.storyboards.storyIsPublic);

    const {userData, config, loading: loadingUserData} = useOptimizedConfig();
    const platformPermissions = usePlatformPermissions(userData);
    const [storyboards, loadingStoryboards, setStoryboards, storyboardsError] = useStoryboardsData();
    const urls = config?.URLS || {};

    const {showError} = useAlert();
    const {navigateToRoot} = useSafeNavigation();
    const errorHandledRef = useRef<boolean>(false);

    useEffect(() => {
        if (storyboardsError && axios.isAxiosError(storyboardsError) && storyboardsError.response?.status === 403 && !errorHandledRef.current) {
            errorHandledRef.current = true;
            showError(i18n.t('tt_storyboard_access_denied.text'));
            navigateToRoot({replace: true});
        }
    }, [storyboardsError, showError, navigateToRoot]);

    useEffect(() => {
        errorHandledRef.current = false;
    }, [activeStoryboardHashid]);


    const objectAPIEndpointUrlMap: Record<string, string> = {
        agents: urls.agentsResource || '',
        customers: urls.customersListResource || '',
        teams: urls.teamsResource || '',
    };

    let objectDetailUrl = '';
    if (activeStoryObject && objectAPIEndpointUrlMap[activeStoryUrl]) {
        objectDetailUrl = `${objectAPIEndpointUrlMap[activeStoryUrl]}/${activeStoryObject}`;
    }

    const [objectNameData] = useData<ObjectNameData>(objectDetailUrl, 'activeStoryObjectDetails');

    useEffect(
        () => {
            setActiveStoryboard([activeStoryboardHashid, activeStoryboardUrl]);
            setActiveDummyStory(activeStoryUrl as any);
            setActiveDummyStoryboard(activeStoryboardUrl);
            setActiveStoryObject(activeStoryObject);
        },
        [
            activeStoryboardHashid,
            activeStoryUrl,
            activeStoryboardUrl,
            activeStoryObject,
            setActiveStoryboard,
            setActiveDummyStory,
            setActiveDummyStoryboard,
            setActiveStoryObject,
        ],
    );

    useEffect(() =>
        () => {
            setActiveStoryboard(['', '']);
            setActiveDummyStory('');
            setActiveStoryObject('');
            setStoryboards([]);
        },
        [
            setStoryboards,
            setActiveStoryboard,
            setActiveDummyStory,
            setActiveStoryObject,
        ]);

    const chartSettingsModalToggle = useCallback(
        () => setChartSettingsModalOpen((prev: boolean) => !prev),
        [setChartSettingsModalOpen],
    );

    const changeChartPosition = (dragIndex: number, hoverIndex: number): void => {
        // Safety check: ensure we have valid storyboards and activeStoryboardIndex
        if (!Array.isArray(storyboards) || activeStoryboardIndex < 0 || activeStoryboardIndex >= storyboards.length) {
            return;
        }

        const updatedStoryboards = produce(storyboards, (draft: StoryboardEntity[]) => {
            if (draft[activeStoryboardIndex] && draft[activeStoryboardIndex].charts) {
                const allChartsData = draft[activeStoryboardIndex].charts;
                const [dragChart] = allChartsData.splice(dragIndex, 1);
                allChartsData.splice(hoverIndex, 0, dragChart);
            }
        });

        setStoryboards(updatedStoryboards);

        // Mark as having unsaved changes if in rearrange mode
        if (isRearrangeMode) {
            setHasUnsavedChanges(true);
        }
    };

    const commitChartReorder = (): void => {
        // Safety check: ensure we have valid storyboards and activeStoryboardIndex
        if (!Array.isArray(storyboards) || activeStoryboardIndex < 0 || activeStoryboardIndex >= storyboards.length) {
            return;
        }

        // Only auto-save if not in rearrange mode
        if (!isRearrangeMode) {
            axios.post(window.URLS.chartReorder, {
                storyboardId: activeStoryboardId,
                sortingOrder: Array.isArray(storyboards[activeStoryboardIndex].charts)
                    ? storyboards[activeStoryboardIndex].charts.map((obj: Chart, idx: number) => ({
                        id: obj.id || 0,
                        sortIndex: idx,
                    }))
                    : [],
            });
        }

        setInGridAnimation();
    };

    const saveChartLayout = (): void => {
        // Safety check: ensure we have valid storyboards and activeStoryboardIndex
        if (!Array.isArray(storyboards) || activeStoryboardIndex < 0 || activeStoryboardIndex >= storyboards.length) {
            return;
        }

        axios.post(window.URLS.chartReorder, {
            storyboardId: activeStoryboardId,
            sortingOrder: Array.isArray(storyboards[activeStoryboardIndex].charts)
                ? storyboards[activeStoryboardIndex].charts.map((obj: Chart, idx: number) => ({
                    id: obj.id || 0,
                    sortIndex: idx,
                }))
                : [],
        });

        setHasUnsavedChanges(false);
        setIsRearrangeMode(false);
        setInGridAnimation();
    };

    const cancelChartRearrange = (): void => {
        // Safety check: ensure we have valid storyboards and activeStoryboardIndex
        if (!Array.isArray(storyboards) || activeStoryboardIndex < 0 || activeStoryboardIndex >= storyboards.length) {
            return;
        }

        // Restore original chart order
        const updatedStoryboards = produce(storyboards, (draft: StoryboardEntity[]) => {
            if (draft[activeStoryboardIndex]) {
                draft[activeStoryboardIndex].charts = [...originalChartOrder];
            }
        });

        setStoryboards(updatedStoryboards);
        setHasUnsavedChanges(false);
        setIsRearrangeMode(false);
        setInGridAnimation();
    };

    const toggleRearrangeMode = (): void => {
        if (isRearrangeMode) {
            // Exiting rearrange mode
            if (hasUnsavedChanges) {
                // Could add a confirmation dialog here
                cancelChartRearrange();
            } else {
                setIsRearrangeMode(false);
            }
        } else {
            // Entering rearrange mode
            // Safety check: ensure we have valid storyboards and activeStoryboardIndex
            if (!Array.isArray(storyboards) || activeStoryboardIndex < 0 || activeStoryboardIndex >= storyboards.length) {
                return;
            }

            setOriginalChartOrder([...storyboards[activeStoryboardIndex].charts]);
            setIsRearrangeMode(true);
            setHasUnsavedChanges(false);
        }
    };

    const deleteChart = (index: number): void => {
        const updatedStoryboards = produce(storyboards, (draft: StoryboardEntity[]) => {
            draft[activeStoryboardIndex].charts.splice(index, 1);
        });

        setStoryboards(updatedStoryboards);
        setInGridAnimation();
    };

    const modifyChart = (chartData: Chart, index: number): void => {
        if (chartData.storyboardId !== activeStoryboardId) {
            deleteChart(index);
            addChart([chartData]);
        } else {
            const updatedStoryboards = produce(storyboards, (draft: StoryboardEntity[]) => {
                const existingChart: any = draft[activeStoryboardIndex].charts[index] || {};
                const currentVersion: number = typeof existingChart.configVersion === 'number'
                    ? existingChart.configVersion
                    : 0;

                draft[activeStoryboardIndex].charts.splice(index, 1, {
                    ...chartData,
                    // Bump local config version so React remounts the cell and its handler.
                    configVersion: currentVersion + 1,
                } as any);
            });

            setStoryboards(updatedStoryboards);
            setInGridAnimation();
        }
    };

    const addStoryboard = (storyboardData: StoryboardEntity): void => {
        if (userIsStoryAdmin) {
            const updatedStoryboards = produce(storyboards, (draft: StoryboardEntity[]) => {
                draft.push(storyboardData);
            });

            setStoryboards(updatedStoryboards);
        }
    };

    const modifyStoryboard = (storyboardData: StoryboardEntity): void => {
        const storyboardIndex = storyboards.findIndex((el: StoryboardEntity) => el.id === storyboardData.id);

        const updatedStoryboards = produce(storyboards, (draft: StoryboardEntity[]) => {
            if (draft[storyboardIndex]) {
                draft[storyboardIndex].title = storyboardData.title;
            }
        });

        setStoryboards(updatedStoryboards);
    };

    const moveStoryboardLink = (dragIndex: number, hoverIndex: number): void => {
        const draggedStoryboardLink = storyboards[dragIndex];

        const updatedStoryboards = produce(storyboards, (draft: StoryboardEntity[]) => {
            draft.splice(dragIndex, 1);
            draft.splice(hoverIndex, 0, draggedStoryboardLink);
        });

        setStoryboards(updatedStoryboards);
    };

    const deleteStoryboard = async (index: number): Promise<void> => {
        const updatedStoryboards = produce(storyboards, (draft: StoryboardEntity[]) => {
            draft.splice(index, 1);
        });

        if (activeStoryboardIndex === index) {
            let nextIndex = index;
            if (index === updatedStoryboards.length) {
                nextIndex = index - 1;
            }

            const url = generateStoryboardUrl(updatedStoryboards[nextIndex], activeStoryUrl, activeStoryObject);

            await navigate(url);
        }

        setStoryboards(updatedStoryboards);
    };

    const commitStoryboardReorder = (): void => {
        axios.post(window.URLS.storyboardReorder, {
            sortingOrder: Array.isArray(storyboards)
                ? storyboards.map((obj: StoryboardEntity, idx: number) => ({
                    id: obj.id,
                    sortIndex: idx,
                }))
                : [],
            storyId: storyboards[0]?.storyId,
        });
    };

    if (loadingStoryboards) {
        return (
            <CellLoader fullHeight={true} />
        );
    }

    if (activeStoryboardIndex === -1 || !storyboards.length || !Array.isArray(storyboards) || activeStoryboardIndex >= storyboards.length) {
        return null;
    }

    let storyboardComponent: React.ReactNode;
    if (storyboards.length && storyboards[activeStoryboardIndex]) {
        const charts = storyboards[activeStoryboardIndex].charts;
        storyboardComponent = (
            <Storyboard
                allChartsData={Array.isArray(charts) ? charts as any : []}
                addChart={() => setChartSettingsModalOpen(true)}
                onNewChartSave={(chartData: Chart, toPosition?: number) => addChart([chartData, toPosition])}
                moveChart={(dragIndex: number, hoverIndex: number) => changeChartPosition(dragIndex, hoverIndex)}
                onChartMove={commitChartReorder}
                modifyChart={(chartData: Chart, index: number) => modifyChart(chartData, index)}
                deleteChart={(index: number) => deleteChart(index)}
                title={storyboards[activeStoryboardIndex].title}
                writeAccess={storyboards[activeStoryboardIndex].writeAccess}
                inGridAnimation={inGridAnimation}
                storyIsPublic={storyIsPublic}
                isRearrangeMode={isRearrangeMode}
            />
        );
    } else {
        storyboardComponent = <h2>{i18n.t('jt_start_creating_storyboard.text')}</h2>;
    }

    if (!loadingUserData) {
        let specialTimeBasedMessage: React.ReactNode = null;

        // Time-based access limit in days (from appConfig store or global FCONST)
        const limitDays: number =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ((appConfig as any)?.FS_DATA_ACCESS_LIMIT_LAST_DAYS ??
                (window as any).FCONST?.FS_DATA_ACCESS_LIMIT_LAST_DAYS ??
                0);
        const dateParts = userData.sessionStartCompDate.split('/');
        const day = dateParts[0];
        const month = dateParts[1];
        const year = dateParts[2];

        const outputDateString = `${year}/${month}/${day}`;

        const sessionStartDate = new Date(outputDateString);

        const limitDate = new Date();

        limitDate.setHours(0, 0, 0, 0);

        limitDate.setDate(limitDate.getDate() - limitDays);

        if (isModuleEnabled(userData, 'addon-time-based-access') && limitDays > 0 && sessionStartDate < limitDate) {
            specialTimeBasedMessage = (
                <div className="alert alert-warning">
                    {i18n.t('jt_access_to_data_limited_to_the_last.text', {days: limitDays})}
                </div>
            );
        }

        return (
            <>
                {
                    platformPermissions.canAccessOlderDataThanLimit ?
                        null : (
                            specialTimeBasedMessage
                        )
                }

                {
                    userData?.indexAnonymized && (
                        <div className="alert alert-warning">
                            {i18n.t('jt_the_data.text')}
                        </div>
                    )
                }

                {
                    objectNameData?.optIn === false && activeStoryUrl === 'customers' && userData.currentModules['coop'] ? (
                        <div className="alert alert-warning text-left">{i18n.t('jt_the_analytics.text')}</div>
                    ) : null
                }

                {
                    objectNameData?.optIn === true && objectNameData?.optInDate && activeStoryUrl === 'customers' ? (
                        <div className="alert alert-warning text-left">
                            The analytics is enabled for events starting from {objectNameData?.optInDate}.</div>
                    ) : null
                }

                {
                    activeStoryUrl === 'customers' ? (
                        <HelpTextBlock helpText={i18n.t('ht_this_view_customer_details.text')} />
                    ) : null
                }

                {
                    window.SCONSTANTS.sessionAgent !== 'true' && activeStoryUrl === 'agents' ? (
                        <HelpTextBlock helpText={i18n.t('ht_the_agent.text')} />
                    ) : null
                }

                {
                    window.SCONSTANTS.sessionAgent === 'true' && activeStoryUrl === 'agents' ? (
                        <HelpTextBlock helpText={i18n.t('ht_this_page.text')} />
                    ) : null
                }

                {
                    activeStoryUrl === 'teams' ? (
                        <HelpTextBlock helpText={i18n.t('ht_the_team_performance.text')} />
                    ) : null
                }

                {
                    storyIsPublic && !(['calls', 'emails', 'chats', 'feedback'].indexOf(storyboards[activeStoryboardIndex].storyAssignedUrl || '') > -1) && !(['customers', 'agents', 'teams', 'calls', 'emails', 'chats', 'feedback'].indexOf(activeStoryUrl) > -1) ? (
                        <HelpTextBlock helpText={i18n.t('ht_use_the.text')} />
                    ) : null
                }

                {
                    (['calls', 'emails', 'chats', 'feedback'].indexOf(storyboards[activeStoryboardIndex].storyAssignedUrl || '') > -1) ? (
                        <HelpTextBlock helpText={i18n.t('ht_feelingstream_event_dashboard.text')} />
                    ) : null
                }

                <div>
                    {!hideNavigation && (
                        <StoryboardMenuBar
                            onNewChartSave={(chartData: Chart) => addChart([chartData])}
                            chartSettingsModalOpen={chartSettingsModalOpen}
                            chartSettingsModalToggle={chartSettingsModalToggle}
                            onNewStoryboardSave={(storyboardData: StoryboardEntity) => addStoryboard(storyboardData)}
                            onStoryboardModify={(storyboardData: StoryboardEntity) => modifyStoryboard(storyboardData)}
                            moveStoryboard={(dragIndex: number, hoverIndex: number) => moveStoryboardLink(dragIndex, hoverIndex)}
                            onStoryboardMove={commitStoryboardReorder}
                            deleteStoryboard={(index: number) => deleteStoryboard(index)}
                            storyboards={storyboards}
                            activeStoryboardIndex={activeStoryboardIndex}
                            storyIsPublic={storyIsPublic}
                            isRearrangeMode={isRearrangeMode}
                            hasUnsavedChanges={hasUnsavedChanges}
                            onToggleRearrangeMode={toggleRearrangeMode}
                            onSaveLayout={saveChartLayout}
                            onCancelRearrange={cancelChartRearrange}
                        />
                    )}

                    {storyboardComponent}

                    <QueryHelpModal />
                </div>
            </>
        );
    } else {
        return (
            <CellLoader fullHeight={true} />
        );
    }
});

const StoryboardView: React.FC<StoryboardViewProps> = React.memo((props) => {
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const userCanAccessStoryboards = hasPermission(
        'fs.story.storyboard.read',
        userData?.permissions,
        true,
    ) || false;

    if (!userCanAccessStoryboards && !loadingUserData) {
        return (
            <div className="alert alert-danger" role="alert">
                {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
            </div>
        );
    }

    return <StoryboardViewContent {...props} />;
});

export default StoryboardView; 